import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, loginWithGoogle } from "@/lib/auth";
import { PageShell, SectionLabel, SlideCorner } from "@/components/Layout";
import { toast } from "sonner";
import { Plus, X, MapPin } from "lucide-react";
import { DISCIPLINE_KEYS, disciplineColor } from "@/lib/disciplines";

const DISCIPLINES = ["all", ...DISCIPLINE_KEYS];
const CITIES = ["all", "mumbai", "delhi", "bangalore", "chennai", "kolkata", "hyderabad", "ahmedabad", "kochi"];

export default function Commons() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [discipline, setDiscipline] = useState("all");
  const [city, setCity] = useState("all");
  const [toggles, setToggles] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [myOffers, setMyOffers] = useState([]);

  useEffect(() => {
    api.get("/commons/offers", { params: { discipline, city } }).then(({ data }) => setItems(data.items || []));
  }, [discipline, city]);

  useEffect(() => {
    if (!user) return;
    api.get("/me/toggles").then(({ data }) => setToggles(data));
    api.get("/commons/offers/me").then(({ data }) => setMyOffers(data.items || []));
  }, [user]);

  const reloadMine = () => api.get("/commons/offers/me").then(({ data }) => setMyOffers(data.items || []));

  return (
    <PageShell>
      <section className="relative grid-bg border-b border-border">
        <SlideCorner right="CREATIVE COMMONS" />
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-20 pb-14">
          <SectionLabel>Creative Commons</SectionLabel>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[7rem] font-bold leading-[0.9] tracking-tighter">
            Members helping <br /> <span className="hl-cyan">members.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-white/85 text-base md:text-lg leading-relaxed">
            Creative Commons is a peer resource network for The Circle and The House. Members offer their skills, gear, studios, time — at <span className="hl-coral">subsidised rates</span> — to other practitioners.
          </p>
          {user && toggles && (
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {toggles.cc_unlocked ? (
                toggles.creative_commons_active ? (
                  <button data-testid="commons-add-offer" onClick={() => setShowForm(true)} className="btn-pill btn-coral">
                    <Plus size={14} /> Add a CC offer
                  </button>
                ) : (
                  <p className="meta-mono !text-white/65">— ENABLE THE BLUE TOGGLE ON YOUR DASHBOARD TO LIST AN OFFER</p>
                )
              ) : (
                <p className="meta-mono !text-white/55">— UPGRADE TO THE CIRCLE TO PARTICIPATE</p>
              )}
            </div>
          )}
          {!user && (
            <button onClick={() => loginWithGoogle("/commons")} data-testid="commons-login" className="btn-pill btn-coral mt-8">Sign in to participate</button>
          )}
        </div>
      </section>

      <section className="border-b border-border sticky top-14 z-30 bg-background/95 backdrop-blur">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-5 flex flex-wrap gap-3 items-center">
          <p className="meta-mono mr-2">— BROWSE</p>
          <Select label="Discipline" value={discipline} onChange={setDiscipline} options={DISCIPLINES} testid="commons-disc" />
          <Select label="City" value={city} onChange={setCity} options={CITIES} testid="commons-city" />
        </div>
      </section>

      <section>
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-14">
          {items.length === 0 ? (
            <div className="border border-dashed border-border p-16 text-center">
              <p className="font-display text-3xl md:text-4xl font-bold text-white/40">No CC offers yet in this slice.</p>
              <p className="meta-mono mt-4">— BE THE FIRST · CIRCLE & HOUSE MEMBERS CAN LIST OFFERS</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border" data-testid="commons-list">
              {items.map((o) => {
                const c = disciplineColor(o.discipline);
                return (
                  <article key={o.offer_id} className="bg-background p-6 group">
                    <div className="flex items-start justify-between gap-3">
                      <p className="meta-mono" style={{ color: c }}>— {(o.discipline || "").toUpperCase()}</p>
                      <span className="meta-mono !text-secondary">{o.rate || "Subsidised"}</span>
                    </div>
                    <h3 className="font-display text-2xl font-bold mt-3 leading-tight">{o.title}</h3>
                    <p className="mt-2 text-white/75 text-sm leading-relaxed">{o.description}</p>
                    <div className="mt-5 flex items-center gap-3 text-white/70 text-xs">
                      {o.user_picture && <img src={o.user_picture} alt="" className="w-7 h-7 rounded-full object-cover" />}
                      <span className="font-mono">{o.user_name}</span>
                      {o.city && <span className="flex items-center gap-1 meta-mono"><MapPin size={10} /> {o.city.toUpperCase()}</span>}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {user && myOffers.length > 0 && (
        <section className="border-t border-border bg-[#0c0c0e]">
          <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12">
            <SectionLabel>Your CC offers</SectionLabel>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
              {myOffers.map((o) => (
                <div key={o.offer_id} className="bg-background p-5">
                  <div className="flex items-start justify-between">
                    <p className="font-display text-lg font-bold">{o.title}</p>
                    <button onClick={async () => { await api.delete(`/commons/offers/${o.offer_id}`); reloadMine(); }} className="text-white/40 hover:text-primary"><X size={14} /></button>
                  </div>
                  <p className="meta-mono mt-2 !text-white/55">— {o.discipline?.toUpperCase()} · {o.rate || "Subsidised"}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {showForm && (
        <OfferModal onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); reloadMine(); api.get("/commons/offers").then(({ data }) => setItems(data.items || [])); }} />
      )}
    </PageShell>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div className="flex items-center gap-2">
      <p className="meta-mono !text-white/55">{label}:</p>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="bg-background border border-border px-3 py-2 font-mono text-xs uppercase tracking-[0.18em] outline-none focus:border-primary">
        {options.map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
      </select>
    </div>
  );
}

function OfferModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", discipline: "music", description: "", rate: "", city: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) { toast.error("Title and description required"); return; }
    setBusy(true);
    try {
      await api.post("/commons/offers", form);
      toast.success("Offer listed.");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not list");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-5" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-background border border-border w-full max-w-xl p-6 md:p-8" data-testid="commons-offer-modal">
        <div className="flex items-center justify-between mb-6">
          <p className="meta-mono !text-primary">— NEW CC OFFER</p>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="grid gap-4">
          <In label="Title" v={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="cco-title" />
          <Sel label="Discipline" v={form.discipline} onChange={(v) => setForm({ ...form, discipline: v })} options={DISCIPLINE_KEYS} testid="cco-discipline" />
          <In label="Subsidised rate (e.g. ₹500/hr, Free for students)" v={form.rate} onChange={(v) => setForm({ ...form, rate: v })} testid="cco-rate" />
          <In label="City" v={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="cco-city" />
          <div>
            <p className="meta-mono mb-2">— WHAT ARE YOU OFFERING?</p>
            <textarea data-testid="cco-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="w-full bg-transparent border border-border p-3 outline-none focus:border-primary text-sm" />
          </div>
          <button disabled={busy} data-testid="cco-submit" className="btn-pill btn-coral mt-2">{busy ? "Listing…" : "List offer"}</button>
        </form>
      </div>
    </div>
  );
}
function In({ label, v, onChange, testid }) {
  return (
    <div>
      <p className="meta-mono mb-2">— {label.toUpperCase()}</p>
      <input data-testid={testid} value={v} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border border-border p-3 outline-none focus:border-primary text-sm" />
    </div>
  );
}
function Sel({ label, v, onChange, options, testid }) {
  return (
    <div>
      <p className="meta-mono mb-2">— {label.toUpperCase()}</p>
      <select data-testid={testid} value={v} onChange={(e) => onChange(e.target.value)} className="w-full bg-background border border-border p-3 outline-none focus:border-primary text-sm">
        {options.map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
      </select>
    </div>
  );
}
